import { useState, useEffect, useCallback } from "react";

export interface AppNotification {
  id: string;
  title: string;
  description: string;
  timestamp: number;
  read: boolean;
  type?: "calendar" | "task" | "info" | "success" | "warning";
  link?: string;
}

const STORAGE_KEY = "dotus_crm_notifications";
const EVENT_NAME = "dotus_notifications_changed";
const MAX_NOTIFICATIONS = 50;

export function getStoredNotifications(): AppNotification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {}
  return [];
}

export function saveStoredNotifications(list: AppNotification[]): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(list.slice(0, MAX_NOTIFICATIONS)),
    );
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch {}
}

/**
 * Adiciona uma notificação ao sino.
 * Evita spam duplicado se a mesma mensagem foi inserida nos últimos 15 segundos.
 */
export function addAppNotification(item: {
  title: string;
  description: string;
  type?: "calendar" | "task" | "info" | "success" | "warning";
  link?: string;
}): void {
  const current = getStoredNotifications();
  const now = Date.now();

  // Evita notificações duplicadas idênticas em curto intervalo
  const isDuplicate = current.some(
    (n) =>
      n.title === item.title &&
      n.description === item.description &&
      now - n.timestamp < 15000,
  );
  if (isDuplicate) return;

  const newNotification: AppNotification = {
    id: `notif_${now}_${Math.random().toString(36).slice(2, 7)}`,
    title: item.title,
    description: item.description,
    timestamp: now,
    read: false,
    type: item.type || "info",
    link: item.link,
  };

  saveStoredNotifications([newNotification, ...current]);
}

export function markAllNotificationsAsRead(): void {
  const current = getStoredNotifications();
  const updated = current.map((n) => ({ ...n, read: true }));
  saveStoredNotifications(updated);
}

export function markNotificationAsRead(id: string): void {
  const current = getStoredNotifications();
  const updated = current.map((n) => (n.id === id ? { ...n, read: true } : n));
  saveStoredNotifications(updated);
}

export function clearAllNotifications(): void {
  saveStoredNotifications([]);
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<AppNotification[]>(() =>
    getStoredNotifications(),
  );

  const refresh = useCallback(() => {
    setNotifications(getStoredNotifications());
  }, []);

  useEffect(() => {
    const handleUpdate = () => refresh();
    window.addEventListener(EVENT_NAME, handleUpdate);
    window.addEventListener("storage", handleUpdate);
    return () => {
      window.removeEventListener(EVENT_NAME, handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, [refresh]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return {
    notifications,
    unreadCount,
    markAllAsRead: markAllNotificationsAsRead,
    markAsRead: markNotificationAsRead,
    clearAll: clearAllNotifications,
    addNotification: addAppNotification,
  };
}
